<!-- =========================================================================================
  File Name: UserList.vue
  Description: User List page
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
  <div id="page-commercial-product-producer-list" class="ag-grid-container">
    <div class="vx-card">
      <grid-panel
          v-if="this.$route.params.id"
          height="md"
          :column-defs="columnDefs"
          :url="(params) => this.listProducers(params, this.$route.params.id, true)"
          :auto-size="true"
          ref="commercialProductProducerGrid"
          title="Assigned Producers"
          @update:gridApi="(gridApi) => this.gridApi = gridApi"
          @rowSelected="onRowSelected"
      >
        <template v-slot:right>
          <yes-no-toggle v-model="onlyShowAssigned" class="flex mr-4">
            <span class="mr-2">Only show assigned</span>
          </yes-no-toggle>
        </template>

        <vs-dropdown-item v-if="!readonly && componentAccess.canEdit.access" :disabled="updatingProducers">
          <span class="flex items-center" @click="assignProducers">
            <feather-icon icon="PlusIcon" svgClasses="h-4 w-4" class="mr-2" />
            <span>Update Assigned Producer(s)</span>
          </span>
        </vs-dropdown-item>
        <vs-dropdown-item v-if="!readonly && componentAccess.canEdit.access" :disabled="applyingProductToAllSalesCountry">
          <span class="flex items-center" @click="addCommercialProductToAllSalesCountry">
            <feather-icon icon="PlusIcon" svgClasses="h-4 w-4" class="mr-2" />
            <span>Apply Commercial Product to All Sales Countries</span>
          </span>
        </vs-dropdown-item>
      </grid-panel>
    </div>
  </div>
</template>
<script>

import '@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss'

// Cell Renderer
import CellMatchingMixin from "@/views/pages/sync/CellMatchingMixin";
import GridPanel from "@/views/components/ag-grid/GridPanel";
import YesNoToggle from "@/views/components/extra-components/YesNoToggle";
import StatBox from "@/views/components/stats/StatBox";
import NotificationMixin from "@/components/mixins/NotificationMixin";
import CanAccessMixin from "@/components/mixins/CanAccessMixin";

export default {
  components: {
    GridPanel,
    YesNoToggle,
    StatBox,
  },
  mixins: [NotificationMixin, CellMatchingMixin, CanAccessMixin],
  props: {
    readonly: {
      type: Boolean,
      required: false,
      default: false
    },
  },
  data () {
    return {
      selectedProducerIds: [],
      searchQuery: '',
      onlyShowAssigned: false,
      gridApi: null,
      updatingProducers: false,
      applyingProductToAllSalesCountry: false,
      componentAccess: {
        canEdit: {
          route: 'commercial-products.producers.edit',
          access: false,
        },
      },
      // AgGrid
      columnDefs: [
        {
          headerName: '',
          field: 'Company_PK',
          width: 50,
          filter: false,
          checkboxSelection: true,
          headerCheckboxSelectionFilteredOnly: true,
          headerCheckboxSelection: true,
          cellClass: 'flex',
          valueGetter: (row) => {
            if (parseInt(row.data.CommercialProduct_PK) === parseInt(this.$route.params.id)) {
              row.node.setSelected(true)
            }
          }
        },
        {
          headerName: 'Producer Code',
          field: 'CompanyCode',
          sort: 'asc',
        },
        {
          headerName: 'Producer',
          field: 'CompanyName',
        },
        {
          headerName: 'Producer Status',
          field: 'Type',
        },
        {
          headerName: 'Assigned',
          field: 'CommercialProduct_PK',
          suppressFiltersToolPanel: true,
          suppressColumnsToolPanel: true,
          valueGetter: ({ data }) => {
            if (parseInt(data.CommercialProduct_PK) === parseInt(this.$route.params.id)) {
              return 'Assigned';
            }

            return '';
          }
        },
      ],
    }
  },
  watch: {
    onlyShowAssigned: {
      handler(newValue) {
        const filterComponent = this.gridApi.getFilterInstance('CommercialProduct_PK');

        if (newValue) {
          filterComponent.setModel({values: [ 'Assigned' ]});
        }

        if (!newValue) {
          filterComponent.setModel(null);
        }

        this.gridApi.onFilterChanged();
      },
    }
  },
  methods: {
    onRowSelected(event) {
      this.toggleProducerSelection(event);
    },
    toggleProducerSelection(params) {
      const isSelected = params.node.isSelected();

      if (isSelected && !this.selectedProducerIds.includes(params.data.Company_PK)) {
        this.selectedProducerIds.push(params.data.Company_PK);
      } 
      
      if (!isSelected) {
        this.selectedProducerIds = this.selectedProducerIds.filter(id => id !== params.data.Company_PK);
      }
    },
    async listProducers(params) {
      const productId = parseInt(this.$route.params.id);
      const data = await this.$api.commercialProducts.listProducers(params, this.$route.params.id, true);

      if (productId) {
        data.data.data.forEach(row => {
          if (parseInt(row.CommercialProduct_PK) === productId) {
            this.selectedProducerIds.push(row.Company_PK);
          }
        });
      }

      return data;
    },
    async assignProducers() {
      this.updatingProducers = true;

      if (this.readOnly) {
        this.notifyError('You do not have permission to do this');
        return false;
      }

      // if (!this.selectedProducerIds.length) {
      //   this.notifyError('Please select at least one producer');
      //   return false;
      // }

      const producerTxt = this.selectedProducerIds.length > 1 ? 'Producers' : 'Producer';

      try {
        this.selectedProducerIds = [...new Set(this.selectedProducerIds)];

        const { data } = await this.$api.commercialProducts.assignProducers(this.selectedProducerIds, this.$route.params.id, true);
        this.$refs.commercialProductProducerGrid.list();
        this.notifySuccess(`${producerTxt} assigned`);
      } catch (e) {
        this.notifyError(`Failed to assign ${producerTxt}`);
        console.error(e)
      } finally {
        this.updatingProducers = false;
      }
    },
    async addCommercialProductToAllSalesCountry() {
      this.applyingProductToAllSalesCountry = true;

      try {
        const { data } = await this.$api.commercialProducts.getSalesCountry(this.$route.params.id);

        if (!data.data || !data.data.SalesCountryType_FK) {
          this.notifyError('Sales Country missing, please add missing information');
          return;
        }

        await this.$api.commercialProducts.addCommercialProductToAllSalesCountry(this.$route.params.id)
        this.$refs.commercialProductProducerGrid.list();
        this.notifySuccess('Commercial Product assigned');
      } catch (e) {
        this.notifyError('Failed to assign Commercial Product');
        console.error(e)
      } finally {
        this.applyingProductToAllSalesCountry = false;
      }
    }
  },
}

</script>

<style lang="scss">

.statistical-box {
  color: black;
  border: 1px solid black;
  padding: 10px;
  text-align: center;
  width: 120px;
  border-radius: 5px;

  .stat-number {
    font-weight: bold;
    font-size: 18px;
  }
}
</style>
